import { useCallback, useMemo } from 'react'
import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
} from '@tanstack/react-query'

import { getExplorerContest } from '@services/contestDiscoveryFeed'
import { queryClient } from '@/lib/queryClient'

import { QueryKeys } from '@/enums'

type UseContestDetails = {
  contestId: UUID
  limit: number
  filter?: UUID
  options?: Omit<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    UseInfiniteQueryOptions<any, Error, any>,
    'queryKey' | 'getNextPageParam' | 'initialPageParam'
  >
}

export const useContestDetails = ({
  contestId,
  limit,
  filter,
  options,
}: UseContestDetails) => {
  const queryKey = useMemo(
    () =>
      [QueryKeys.ContestDetails, contestId, filter].filter((item) => !!item),
    [contestId, filter]
  )

  const { data, fetchNextPage, hasNextPage, isLoading, refetch, error } =
    useInfiniteQuery({
      queryKey,
      queryFn: async ({ pageParam }) => {
        const idLte = filter && !pageParam ? { id_lte: filter } : null
        const result = await getExplorerContest(contestId, {
          id_lt: (pageParam as string) || '',
          limit,
          ...idLte,
        })

        return result
      },
      initialPageParam: '',
      getNextPageParam: (lastPage) => lastPage?.lastKey || undefined,
      ...options,
    })

  const refetchFirstPage = useCallback(async () => {
    queryClient.setQueryData<{
      pages: unknown[]
      pageParams: (string | undefined)[]
    }>(queryKey, (data) => ({
      pages: data?.pages?.slice(0, 1) || [],
      pageParams: data?.pageParams?.slice(0, 1) || [],
    }))
    await refetch()
  }, [queryKey, refetch])

  const contestDetails = useMemo(() => {
    const result = { ...data?.pages?.[0]?.results }
    const participants = data?.pages
      .flatMap((page) => page.results)
      .flatMap((results) => results.participants)

    return {
      ...result,
      participants,
    }
  }, [data?.pages])

  return {
    contestDetails,
    hasNextPage,
    fetchNextPage,
    refetchFirstPage,
    isLoading,
    error,
  }
}
